import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  CalendarDays,
  Eye,
  Copy,
  X,
  Check,
  Truck,
  ChevronDown,
  Calendar,
  Tag,
  Layout,
  CreditCard,
  User,
  Loader,
  CheckCircle,
  Clock,
  PieChart,
  LogIn,
} from "lucide-react";
import * as XLSX from "xlsx";
import { message, DatePicker } from "antd";
import dayjs from "dayjs";
import DateRangeFilter from "../components/DateRangeFilter";
import ExpandableSearchBar from "../components/ExpandableSearchBar";
import {
  ordersApi,
  employeesApi,
  productsApi,
  orderCampaignApi,
} from "../services/api";
import { useTheme } from "../context/ThemeContext";
import ThemeSelect from "../components/ThemeSelect";
import Pagination from "../components/Pagination";
import ExcelExportButton from "../components/ExcelExportButton";
import { usePermissions } from "../hooks/Usepermissions";
import { formatDate } from "../utils/helpers";

const { RangePicker } = DatePicker;

// ─── Checkbox Component ──────────────────────────────────────────────────────
const ThemeCheckbox = ({ checked, onChange, isDark }) => {
  const { currentTheme } = useTheme();
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer transition-all border-2 ${
        checked
          ? "border-transparent"
          : isDark
            ? "border-white/30 hover:border-white/50"
            : "border-gray-300 hover:border-gray-400"
      }`}
      style={{ backgroundColor: checked ? currentTheme.color : "transparent" }}
    >
      {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={4} />}
    </div>
  );
};

// ─── AWB Masking ─────────────────────────────────────────────────────────────
const maskAWBNumber = (awb) => {
  if (!awb || awb === "-") return "-";
  const s = String(awb);
  if (s.length <= 4) return s;
  return "x".repeat(s.length - 4) + s.slice(-4);
};

// ─── Date Range Resolver ─────────────────────────────────────────────────────
const resolveDateRange = (preset, customStart = "", customEnd = "") => {
  const now = new Date();
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  switch (preset) {
    case "today":
      return { startDate: fmt(now), endDate: fmt(now) };
    case "yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { startDate: fmt(y), endDate: fmt(y) };
    }
    case "this_week": {
      const s = new Date(now);
      s.setDate(s.getDate() - 6);
      return { startDate: fmt(s), endDate: fmt(now) };
    }
    case "this_month":
      return {
        startDate: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    case "this_year":
      return {
        startDate: fmt(new Date(now.getFullYear(), 0, 1)),
        endDate: fmt(new Date(now.getFullYear(), 11, 31)),
      };
    case "custom":
      return { startDate: customStart || "", endDate: customEnd || "" };
    default:
      return { startDate: "", endDate: "" };
  }
};

// ─── Tab Config ──────────────────────────────────────────────────────────────
const STATUS_TABS = [
  { key: "Processing Order", label: "Processing Order" },
  { key: "Confirmed", label: "Confirmed" },
  { key: "Delivered", label: "Delivered" },
  { key: "No response", label: "No response" },
  { key: "Call back", label: "Call back" },
  { key: "RTO Hold", label: "RTO Hold" },
];

const DATE_RANGE_OPTIONS = [
  { value: "allOrders", label: "All View" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

const CUSTOMER_STATUS_OPTIONS = [
  { value: "Delivered", label: "Delivered" },
  { value: "Confirmed", label: "Confirmed" },
  { value: "No response", label: "No response" },
  { value: "Call back", label: "Call back" },
  { value: "RTO Hold", label: "RTO Hold" },
];

const COURIER_OPTIONS = [
  "All courier",
  "DNST",
  "Ekart",
  "E-kart",
  "F-ship",
  "Shadowfax",
  "Speed post",
];

const EMPTY_FILTERS = {
  date: "",
  customStartDate: "",
  customEndDate: "",
  agent: "",
  agentName: "",
  category: "",
  paymentMode: "",
  campaign: "",
  customerStatus: "",
  courier: "",
  cPartner: "",
  ofdBy: "",
  ofdByName: "",
};

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function OFDOrders() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === "#FFFFFF";
  const { has, isAdmin } = usePermissions();
  const navigate = useNavigate();

  // ─── State ────────────────────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderLimit, setOrderLimit] = useState("20 Orders");
  const [selectedCourier, setSelectedCourier] = useState("All courier");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  // Tab
  const [selectedStatusTab, setSelectedStatusTab] =
    useState("Processing Order");
  const [tabCounts, setTabCounts] = useState({});
  const [tabCountsLoading, setTabCountsLoading] = useState(false);

  // Dashboard summary (top cards)
  const [dashboardSummary, setDashboardSummary] = useState(null);

  // Date range
  const [selectedDateRange, setSelectedDateRange] = useState("allOrders");
  const [selectedDateRangeLabel, setSelectedDateRangeLabel] =
    useState("All View");
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Filter modal
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    agents: [],
    categories: [],
    campaigns: [],
    ofdBys: [],
  });
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const [tempFilters, setTempFilters] = useState(EMPTY_FILTERS);
  const [isDownloading, setIsDownloading] = useState(false);

  // ─── Refs (stale closure fix) ─────────────────────────────────────────────
  const currentPageRef = useRef(1);
  const selectedDateRangeRef = useRef("allOrders");
  const customStartDateRef = useRef("");
  const customEndDateRef = useRef("");
  const activeFiltersRef = useRef(EMPTY_FILTERS);
  const dateDropdownRef = useRef(null);

  // Keep refs in sync
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);
  useEffect(() => {
    selectedDateRangeRef.current = selectedDateRange;
  }, [selectedDateRange]);
  useEffect(() => {
    customStartDateRef.current = customStartDate;
  }, [customStartDate]);
  useEffect(() => {
    customEndDateRef.current = customEndDate;
  }, [customEndDate]);

  // ─── NDR agent check ──────────────────────────────────────────────────────
  const isNdrAgent = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const role = (user?.role || "").toUpperCase();
      return role.includes("NDR") || has("NDR_VIEW");
    } catch {
      return false;
    }
  }, [has]);

  // ─── Admin check ──────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      setIsUserAdmin(user?.role === "ADMIN");
    } catch {
      setIsUserAdmin(false);
    }
  }, []);

  // ─── Click outside date dropdown ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(e.target)
      )
        setIsDateDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ─── On mount ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchFilterOptions();
    fetchEmployees();
    fetchDashboardSummary();
    fetchTabCounts(); // ✅ NEW — single API call for all tab counts
    fetchOFDOrders({ overridePage: 1 });
  }, []);

  // Mount par upar wala effect fetchOFDOrders call karta hai. Ye dono effects
  // bhi mount par chalte the → ek hi API 3 baar hit hoti thi. Pehla render skip.
  const skipFirstCourier = useRef(true);
  const skipFirstSearch = useRef(true);

  // ─── Courier / tab change ─────────────────────────────────────────────────
  useEffect(() => {
    if (skipFirstCourier.current) {
      skipFirstCourier.current = false;
      return;
    }
    setCurrentPage(1);
    currentPageRef.current = 1;
    fetchOFDOrders({ overridePage: 1 });
  }, [selectedCourier]);

  // ─── Search debounce ──────────────────────────────────────────────────────
  useEffect(() => {
    if (skipFirstSearch.current) {
      skipFirstSearch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      currentPageRef.current = 1;
      fetchOFDOrders({ overridePage: 1 });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ═══════════════════════════════════════════════════════════════════════════
  //  API CALLS
  // ═══════════════════════════════════════════════════════════════════════════

  // ─── Fetch Tab Counts (Parallel lightweight calls to getOFDOrders) ───────
  const fetchTabCounts = async () => {
    try {
      setTabCountsLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const checkAdmin =
        isAdmin || isUserAdmin || ["ADMIN"].includes(user?.role?.toUpperCase());

      const baseParams = {
        page: 0,
        size: 1,
        showAll: checkAdmin ? "true" : "false",
      };

      if (!checkAdmin) {
        const empId = user?.id || user?.employeeId || user?.empId;
        if (empId) baseParams.employeeId = empId;
      }

      if (selectedCourier !== "All courier")
        baseParams.courier = selectedCourier;
      if (searchTerm?.trim()) baseParams.searchTerm = searchTerm.trim();

      const promises = STATUS_TABS.map(async (tab) => {
        const p = { ...baseParams };
        if (tab.key === "Processing Order") {
          p.customerStatus = "Processing Order";
        } else {
          p.customerStatus = tab.key;
        }

        try {
          const res = await ordersApi.getOFDOrders(p);
          const count =
            res?.total ??
            res?.totalElements ??
            res?.totalCount ??
            (Array.isArray(res?.orders)
              ? res.orders.length
              : Array.isArray(res?.data)
                ? res.data.length
                : Array.isArray(res)
                  ? res.length
                  : 0);
          return { [tab.key]: count };
        } catch {
          return { [tab.key]: 0 };
        }
      });

      const results = await Promise.all(promises);
      const mergedCounts = Object.assign({}, ...results);
      setTabCounts(mergedCounts);
    } catch (err) {
      console.error("fetchTabCounts error:", err);
    } finally {
      setTabCountsLoading(false);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      const res = await ordersApi.getOFDDashboardSummary();
      if (res) setDashboardSummary(res);
    } catch (err) {
      console.error("fetchDashboardSummary error:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await ordersApi.getNDREmployees();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.employees)
            ? res.employees
            : Array.isArray(res?.content)
              ? res.content
              : [];
      setEmployees(list);
    } catch (err) {
      console.error("fetchEmployees error:", err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [empRes, catRes, campRes] = await Promise.allSettled([
        employeesApi.getAllEmployees(),
        productsApi.getAllCategories(),
        orderCampaignApi.getAllCampaigns(),
      ]);

      const empRaw = empRes.status === "fulfilled" ? empRes.value : [];
      const agents = (Array.isArray(empRaw) ? empRaw : [])
        .map((e) => ({
          name: e.name || e.employeeName || e.fullName,
          empId: e.id,
        }))
        .filter((e) => e.name);

      const catRaw = catRes.status === "fulfilled" ? catRes.value : [];
      const categories = [
        ...new Set(
          (Array.isArray(catRaw) ? catRaw : [])
            .map((c) =>
              typeof c === "string" ? c : c?.categoryName || c?.name,
            )
            .filter(Boolean),
        ),
      ].sort();

      const campRaw = campRes.status === "fulfilled" ? campRes.value : [];
      const campaigns = [
        ...new Set(
          (Array.isArray(campRaw) ? campRaw : [])
            .map((c) => c?.campaignName || c?.name)
            .filter(Boolean),
        ),
      ].sort();

      setFilterOptions({ agents, categories, campaigns, ofdBys: agents });
    } catch (err) {
      console.error("fetchFilterOptions error:", err);
    }
  };

  // ─── MAIN FETCH ──────────────────────────────────────────────────────────
  const fetchOFDOrders = async ({
    overrideDateRange,
    overrideStart,
    overrideEnd,
    overridePage,
    overrideFilters,
    overrideSize,
    overrideStatusTab,
  } = {}) => {
    try {
      setLoading(true);
      const page =
        overridePage !== undefined ? overridePage : currentPageRef.current;
      const filters =
        overrideFilters !== undefined
          ? overrideFilters
          : activeFiltersRef.current;
      const dateRange =
        overrideDateRange !== undefined
          ? overrideDateRange
          : selectedDateRangeRef.current;
      const startInput =
        overrideStart !== undefined
          ? overrideStart
          : customStartDateRef.current;
      const endInput =
        overrideEnd !== undefined ? overrideEnd : customEndDateRef.current;
      const currentTab =
        overrideStatusTab !== undefined ? overrideStatusTab : selectedStatusTab;

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const checkAdmin =
        isAdmin || isUserAdmin || ["ADMIN"].includes(user?.role?.toUpperCase());

      const params = {
        page: page - 1,
        size:
          overrideSize !== undefined
            ? parseInt(overrideSize)
            : parseInt(orderLimit),
        showAll: checkAdmin ? "true" : "false",
      };

      if (!checkAdmin) {
        const empId = user?.id || user?.employeeId || user?.empId;
        if (empId) params.employeeId = empId;
      }

      if (selectedCourier !== "All courier") params.courier = selectedCourier;
      if (searchTerm?.trim()) params.searchTerm = searchTerm.trim();

      // Date range
      if (dateRange && dateRange !== "allOrders") {
        const { startDate, endDate } = resolveDateRange(
          dateRange,
          startInput,
          endInput,
        );
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }

      // Extra filters
      if (filters.agent) params.agentId = filters.agent;
      if (filters.category) params.category = filters.category;
      if (filters.paymentMode) params.paymentMode = filters.paymentMode;
      if (filters.campaign) params.campaign = filters.campaign;
      if (filters.courier && filters.courier !== "All courier")
        params.courier = filters.courier;
      if (filters.cPartner) params.courierPartner = filters.cPartner;
      if (filters.ofdBy) params.ofdById = filters.ofdBy;

      // ✅ Tab filter — customerStatus field use karo
      if (currentTab === "Processing Order") {
        // Backend pe customerStatus blank/null → Processing Order orders
        params.customerStatus = "Processing Order";
      } else {
        params.customerStatus = currentTab;
      }

      const response = await ordersApi.getOFDOrders(params);

      const ordersData = Array.isArray(response)
        ? response
        : Array.isArray(response?.orders)
          ? response.orders
          : Array.isArray(response?.content)
            ? response.content
            : Array.isArray(response?.data)
              ? response.data
              : [];

      const rawTotal =
        response?.total ??
        response?.totalElements ??
        response?.totalCount ??
        ordersData.length;

      setOrders(ordersData);
      setTotalOrders(rawTotal);

      // Update this tab's count
      setTabCounts((prev) => ({ ...prev, [currentTab]: rawTotal }));
    } catch (err) {
      console.error("fetchOFDOrders error:", err);
      message.error("Failed to fetch OFD orders");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  //  HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleTabChange = (tab) => {
    setSelectedStatusTab(tab);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setSelectedOrders([]);

    const updatedFilters = {
      ...activeFiltersRef.current,
      customerStatus: tab === "Processing Order" ? "" : tab,
    };
    setActiveFilters(updatedFilters);
    activeFiltersRef.current = updatedFilters;

    fetchOFDOrders({
      overrideFilters: updatedFilters,
      overridePage: 1,
      overrideStatusTab: tab,
    });
  };

  const handleDateRangeFilter = (rangeType, label) => {
    if (rangeType === "custom") {
      setIsDateDropdownOpen(false);
      setShowCustomDateModal(true);
      return;
    }
    setSelectedDateRange(rangeType);
    selectedDateRangeRef.current = rangeType;
    setSelectedDateRangeLabel(label || "All View");
    setIsDateDropdownOpen(false);
    setCurrentPage(1);
    currentPageRef.current = 1;

    const { startDate, endDate } = resolveDateRange(rangeType);
    fetchOFDOrders({
      overrideDateRange: rangeType,
      overrideStart: startDate,
      overrideEnd: endDate,
      overridePage: 1,
    });
  };

  const handleCustomDateApply = () => {
    if (!customStartDate || !customEndDate) {
      alert("Please select both dates.");
      return;
    }
    if (customEndDate < customStartDate) {
      alert("End date must be >= start date.");
      return;
    }

    const lbl = `${new Date(customStartDate).toLocaleDateString("en-GB")} - ${new Date(customEndDate).toLocaleDateString("en-GB")}`;
    setSelectedDateRange("custom");
    selectedDateRangeRef.current = "custom";
    setSelectedDateRangeLabel(lbl);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setShowCustomDateModal(false);

    fetchOFDOrders({
      overrideDateRange: "custom",
      overrideStart: customStartDate,
      overrideEnd: customEndDate,
      overridePage: 1,
    });
  };

  // ✅ FIXED — Sirf customerStatus field bhejo, status nahi
  const handleCustomerStatusUpdate = async (orderId, status) => {
    try {
      const notesMap = {
        Confirmed: "Customer agreed for re-delivery",
        Delivered: "Successfully delivered to customer",
        "No response": "Customer not responding to calls",
        "Call back": "Customer requested callback",
        "RTO Hold": "Placed on RTO hold",
      };

      const payload = {
        status: status,
        notes: notesMap[status] || `Status updated to: ${status}`,
      };

      await ordersApi.updateOFDCustomerStatus(orderId, payload);
      message.success(`Status updated to "${status}"`);

      // ✅ Sirf DELIVERED aur NOT INTERESTED pe order hatao
      const removeFromList = ["Delivered", "Not interested", "Refused"];

      if (removeFromList.includes(status)) {
        // Order list se hatao
        setOrders((prev) => prev.filter((o) => o.id !== orderId));
        setTotalOrders((prev) => Math.max(0, prev - 1));
      } else {
        // ✅ Order list mein rakho, sirf customerStatus update karo
        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId ? { ...o, customerStatus: status } : o,
          ),
        );
      }

      // Tab counts update
      setTabCounts((prev) => ({
        ...prev,
        [selectedStatusTab]: Math.max(0, (prev[selectedStatusTab] || 1) - 1),
        [status]: (prev[status] || 0) + 1,
      }));

      fetchDashboardSummary();
    } catch (err) {
      console.error("handleCustomerStatusUpdate error:", err);
      message.error(err.message || "Failed to update status");
      fetchOFDOrders();
    }
  };

  const handleAssignOrders = async () => {
    if (selectedOrders.length === 0) {
      message.warning("Please select at least one order");
      return;
    }
    if (!selectedEmployee) {
      message.warning("Please select an employee to assign");
      return;
    }

    const empId =
      selectedEmployee.id ??
      selectedEmployee.employeeId ??
      selectedEmployee.empId;
    try {
      await ordersApi.assignOFDOrders({
        orderIds: selectedOrders,
        assignedToEmployeeId: empId,
      });
      message.success(`${selectedOrders.length} order(s) assigned`);
      setSelectedOrders([]);
      setSelectedEmployee(null);
      fetchOFDOrders();
      fetchDashboardSummary();
    } catch (err) {
      console.error("handleAssignOrders error:", err);
      message.error(err.message || "Failed to assign orders");
    }
  };

  const handleViewDetails = (order) => {
    navigate(`/orders/amount-decrease/${order.id}`, {
      state: { activeTab: "OUT_FOR_DELIVERY" },
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Copied!");
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === orders.length && orders.length > 0)
      setSelectedOrders([]);
    else setSelectedOrders(orders.map((o) => o.id));
  };

  const handleSelectOrder = (id) =>
    setSelectedOrders((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleApplyFilters = () => {
    setActiveFilters(tempFilters);
    activeFiltersRef.current = tempFilters;
    setCurrentPage(1);
    currentPageRef.current = 1;
    setIsFilterModalOpen(false);
    fetchOFDOrders({ overrideFilters: tempFilters, overridePage: 1 });
  };

  const handleResetFilters = () => {
    setTempFilters(EMPTY_FILTERS);
    setActiveFilters(EMPTY_FILTERS);
    activeFiltersRef.current = EMPTY_FILTERS;
    setCurrentPage(1);
    currentPageRef.current = 1;
    setIsFilterModalOpen(false);
    fetchOFDOrders({ overrideFilters: EMPTY_FILTERS, overridePage: 1 });
  };

  // ─── Excel Export ─────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    if (!orders.length) {
      message.warning("No orders to export");
      return;
    }
    try {
      setIsDownloading(true);
      const data = orders.map((o) => ({
        "Order ID": o.orderId || o.id,
        Date:
          o.orderDate || o.createdAt
            ? formatDate(o.orderDate || o.createdAt)
            : "N/A",
        "Customer Name": o.customerName || "N/A",
        Phone: o.contactNumber || "N/A",
        Product: Array.isArray(o.orderItems)
          ? o.orderItems.map((i) => i.productName).join(", ")
          : "-",
        Category: o.category || "N/A",
        "Rest Amount": o.restAmount || o.grandTotal || 0,
        "Payment Mode": o.paymentMode || "COD",
        Campaign: o.campaignName || "-",
        Status: o.status || "OFD",
        "Customer Status": o.customerStatus || "-",
        "OFD By": o.ofdAssignedToName || "-",
        Courier: o.courierName || "-",
        "AWB Number": o.awbNumber || "-",
        Remarks: o.remarks || "",
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "OFD Orders");
      XLSX.writeFile(
        wb,
        `OFD_Orders_${selectedStatusTab}_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      message.success("Downloaded successfully");
    } catch (err) {
      console.error("Excel error:", err);
      message.error("Failed to download");
    } finally {
      setIsDownloading(false);
    }
  };

  // ─── Unique values for filter dropdowns ──────────────────────────────────
  const uniqueCouriers = [
    ...new Set(orders.map((o) => o.courierName).filter(Boolean)),
  ];
  const uniqueCPartners = [
    ...new Set(orders.map((o) => o.courierName).filter(Boolean)),
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div
      className={`min-h-screen p-6 ${isDark ? "bg-[#0f172a]" : "bg-gray-50"}`}
    >
      <div className="flex flex-col gap-6">
        {/* ── Filter Modal ── */}
        {isFilterModalOpen && (
          <div
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsFilterModalOpen(false);
            }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999] p-4 overflow-y-auto"
          >
            <div className="flex items-center justify-center min-h-full">
              <div className="bg-white rounded-xl w-full max-w-2xl mx-auto flex flex-col shadow-xl my-8">
                <div
                  className="flex items-center justify-between px-6 py-4 border-b"
                  style={{ background: currentTheme.color }}
                >
                  <h2 className="text-lg font-bold text-white">
                    Filter Orders
                  </h2>
                  <button
                    onClick={() => setIsFilterModalOpen(false)}
                    className="p-1 hover:bg-white/20 rounded"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Date Range */}
                    <div>
                      <DateRangeFilter
                        label="Date Range"
                        preset={tempFilters.date}
                        startDate={tempFilters.customStartDate}
                        endDate={tempFilters.customEndDate}
                        onChange={({ preset, startDate, endDate }) =>
                          setTempFilters((p) => ({
                            ...p,
                            date: preset,
                            customStartDate: startDate,
                            customEndDate: endDate,
                          }))
                        }
                      />
                    </div>

                    {/* Agent */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agent
                      </label>
                      <ThemeSelect
                        value={tempFilters.agentName || ""}
                        onChange={(val) => {
                          const found = filterOptions.agents.find(
                            (a) => a.name === val,
                          );
                          setTempFilters((p) => ({
                            ...p,
                            agent: found?.empId || "",
                            agentName: val,
                          }));
                        }}
                        options={[
                          { label: "All Agents", value: "" },
                          ...filterOptions.agents.map((a) => ({
                            label: a.name,
                            value: a.name,
                          })),
                        ]}
                        icon={User}
                        className="w-full"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <ThemeSelect
                        value={tempFilters.category}
                        onChange={(val) =>
                          setTempFilters((p) => ({ ...p, category: val }))
                        }
                        options={[
                          { label: "All Categories", value: "" },
                          ...filterOptions.categories.map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                        icon={Tag}
                        className="w-full"
                      />
                    </div>

                    {/* Payment Mode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Mode
                      </label>
                      <ThemeSelect
                        value={tempFilters.paymentMode}
                        onChange={(val) =>
                          setTempFilters((p) => ({ ...p, paymentMode: val }))
                        }
                        options={[
                          { label: "All Modes", value: "" },
                          { label: "COD", value: "COD" },
                          { label: "Prepaid", value: "PREPAID" },
                        ]}
                        icon={CreditCard}
                        className="w-full"
                      />
                    </div>

                    {/* Campaign */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Campaign
                      </label>
                      <ThemeSelect
                        value={tempFilters.campaign}
                        onChange={(val) =>
                          setTempFilters((p) => ({ ...p, campaign: val }))
                        }
                        options={[
                          { label: "All Campaigns", value: "" },
                          ...filterOptions.campaigns.map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                        icon={Layout}
                        className="w-full"
                      />
                    </div>

                    {/* Courier */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Courier
                      </label>
                      <ThemeSelect
                        value={tempFilters.courier}
                        onChange={(val) =>
                          setTempFilters((p) => ({ ...p, courier: val }))
                        }
                        options={[
                          { label: "All Couriers", value: "" },
                          ...uniqueCouriers.map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                        icon={Truck}
                        className="w-full"
                      />
                    </div>

                    {/* Courier Partner */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Courier Partner
                      </label>
                      <ThemeSelect
                        value={tempFilters.cPartner}
                        onChange={(val) =>
                          setTempFilters((p) => ({ ...p, cPartner: val }))
                        }
                        options={[
                          { label: "All Partners", value: "" },
                          ...uniqueCPartners.map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                        icon={Truck}
                        className="w-full"
                      />
                    </div>

                    {/* OFD By */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OFD By
                      </label>
                      <ThemeSelect
                        value={tempFilters.ofdByName || ""}
                        onChange={(val) => {
                          const found = filterOptions.ofdBys.find(
                            (e) => e.name === val,
                          );
                          setTempFilters((p) => ({
                            ...p,
                            ofdBy: found?.empId || "",
                            ofdByName: val,
                          }));
                        }}
                        options={[
                          { label: "All", value: "" },
                          ...filterOptions.ofdBys.map((e) => ({
                            label: e.name,
                            value: e.name,
                          })),
                        ]}
                        icon={User}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end px-6 py-4 border-t bg-gray-50 space-x-3 rounded-b-xl">
                  <button
                    onClick={handleResetFilters}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleApplyFilters}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg"
                    style={{ backgroundColor: currentTheme.color }}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Top Cards (Admin only) ── */}
        {(isAdmin || isUserAdmin) && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 mb-1">
            {[
              {
                label: "VERIFIED TODAY",
                value: dashboardSummary?.verifiedToday,
                icon: CheckCircle,
                color: "bg-blue-500",
              },
              {
                label: "TOTAL OFD TODAY",
                value: dashboardSummary?.totalOfdToday,
                icon: Truck,
                color: "bg-emerald-500",
              },
              {
                label: "PENDING NDR",
                value: dashboardSummary?.pendingNdr,
                icon: Clock,
                color: "bg-amber-500",
              },
              {
                label: "RECOVERY RATE",
                value:
                  dashboardSummary?.recoveryRate != null
                    ? `${dashboardSummary.recoveryRate}%`
                    : "0%",
                icon: PieChart,
                color: "bg-pink-500",
              },
              {
                label: "ACTIVE AGENTS",
                value: dashboardSummary?.activeAgents,
                icon: User,
                color: "bg-indigo-500",
              },
              {
                label: "LOGGED IN",
                value: dashboardSummary?.loggedInAgents,
                icon: LogIn,
                color: "bg-teal-600",
              },
            ].map(({ label, value, icon: Icon, color }) => (
              <div
                key={label}
                className={`p-3.5 rounded-xl border flex items-center gap-3 shadow-xs ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-100"}`}
              >
                <div
                  className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center text-white flex-shrink-0`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p
                    className={`text-[10px] font-bold uppercase tracking-wide leading-tight ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {label}
                  </p>
                  <p
                    className={`text-lg font-extrabold leading-tight ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {value != null ? String(value) : "0"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1
              className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-800"}`}
            >
              OFD{" "}
              <span className="text-gray-500 text-xl">
                ({totalOrders.toLocaleString()})
              </span>
            </h1>

            {/* Date Range Pill */}
            <div className="relative" ref={dateDropdownRef}>
              <button
                onClick={() => setIsDateDropdownOpen((p) => !p)}
                className={`flex items-center border rounded-full px-4 py-1.5 shadow-sm cursor-pointer transition-all ${isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-gray-300 hover:border-gray-400"}`}
              >
                <CalendarDays className="w-5 h-5 text-orange-400 mr-2" />
                <span
                  className={`text-[15px] font-medium ${isDark ? "text-gray-200" : "text-gray-700"}`}
                >
                  {selectedDateRangeLabel}
                </span>
                <ChevronDown
                  className={`w-4 h-4 ml-2 transition-transform ${isDateDropdownOpen ? "rotate-180" : ""}`}
                  style={{ color: currentTheme.color }}
                />
              </button>
              {isDateDropdownOpen && (
                <div
                  className={`absolute left-0 top-full mt-2 rounded-2xl shadow-xl border py-3 z-50 w-56 ${isDark ? "bg-[#1f2937] border-white/10" : "bg-white border-gray-100"}`}
                >
                  {DATE_RANGE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() =>
                        handleDateRangeFilter(opt.value, opt.label)
                      }
                      className={`w-full px-5 py-2.5 text-left text-sm transition-colors ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
                      style={{
                        backgroundColor:
                          selectedDateRange === opt.value
                            ? `${currentTheme.color}25`
                            : undefined,
                        color:
                          selectedDateRange === opt.value
                            ? currentTheme.color
                            : isDark
                              ? "#e5e7eb"
                              : "#374151",
                        fontWeight:
                          selectedDateRange === opt.value ? "bold" : "normal",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {has("OFD_VIEW") && (
              <ExcelExportButton
                onClick={handleDownloadExcel}
                isDownloading={isDownloading}
              />
            )}
            {has("OFD_VIEW") && (
              <button
                onClick={() => {
                  setTempFilters(activeFilters);
                  setIsFilterModalOpen(true);
                }}
                className={`p-2 shadow-sm border rounded-lg transition-all ${isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-gray-200 hover:bg-gray-50"}`}
              >
                <Filter
                  className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-600"}`}
                />
              </button>
            )}
          </div>
        </div>

        {/* ── Custom Date Modal ── */}
        {showCustomDateModal && (
          <div
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowCustomDateModal(false);
            }}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[9999]"
          >
            <div
              className={`rounded-2xl shadow-2xl w-full max-w-md p-6 ${isDark ? "bg-[#1f2937] text-white" : "bg-white text-gray-800"}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Select Custom Date Range</h3>
                <button
                  onClick={() => setShowCustomDateModal(false)}
                  className={`p-1.5 rounded-lg transition-colors ${isDark ? "text-gray-400 hover:text-white hover:bg-white/10" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Start Date – End Date
                </label>
                <RangePicker
                  value={
                    customStartDate && customEndDate
                      ? [dayjs(customStartDate), dayjs(customEndDate)]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setCustomStartDate(dates[0].format("YYYY-MM-DD"));
                      setCustomEndDate(dates[1].format("YYYY-MM-DD"));
                    } else {
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }
                  }}
                  format="DD-MM-YYYY"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "10px",
                  }}
                  size="large"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCustomDateModal(false)}
                  className={`px-5 py-2 text-sm font-semibold rounded-xl ${isDark ? "bg-white/5 text-gray-400 hover:bg-white/10" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomDateApply}
                  disabled={!customStartDate || !customEndDate}
                  className="px-5 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Status Tabs ── */}
        {(isNdrAgent || isAdmin || isUserAdmin || has("OFD_VIEW")) && (
          <div
            className={`flex items-center gap-8 border-b pb-1 pt-2 my-3 overflow-x-auto ${isDark ? "border-white/10" : "border-gray-200"}`}
          >
            {STATUS_TABS.map((tab) => {
              const isSelected = selectedStatusTab === tab.key;
              const count = tabCounts[tab.key] ?? 0;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`pb-2 text-sm font-semibold whitespace-nowrap transition-all border-b-2 flex items-center gap-1.5 ${
                    isSelected
                      ? isDark
                        ? "text-teal-400 border-teal-400"
                        : "text-teal-600 border-teal-600"
                      : isDark
                        ? "border-transparent text-gray-400 hover:text-gray-200"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  style={{
                    borderColor: isSelected
                      ? currentTheme.color
                      : "transparent",
                    color: isSelected ? currentTheme.color : undefined,
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={
                      isSelected
                        ? {
                            backgroundColor: `${currentTheme.color}20`,
                            color: currentTheme.color,
                          }
                        : {
                            backgroundColor: isDark
                              ? "rgba(255,255,255,0.05)"
                              : "#f3f4f6",
                            color: isDark ? "#9ca3af" : "#4b5563",
                          }
                    }
                  >
                    {tabCountsLoading ? "..." : count.toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Filters Row ── */}
        <div className="flex items-center space-x-4 flex-wrap gap-y-3">
          <ExpandableSearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by lead id or customer name"
          />
          <ThemeSelect
            value={orderLimit}
            onChange={(val) => {
              setOrderLimit(val);
              setCurrentPage(1);
              currentPageRef.current = 1;
              fetchOFDOrders({ overridePage: 1, overrideSize: val });
            }}
            options={["20 Orders", "50 Orders", "100 Orders"]}
            className="w-44"
          />
          <ThemeSelect
            value={selectedCourier}
            onChange={(val) => setSelectedCourier(val)}
            options={COURIER_OPTIONS}
            icon={Truck}
            className="w-48"
          />
        </div>

        {/* ── Main Content ── */}
        <div className="flex gap-4">
          {/* ── Orders Table ── */}
          <div
            className={`rounded-xl border overflow-hidden flex-1 ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1400px]">
                <thead
                  className="text-white"
                  style={{ background: currentTheme.color }}
                >
                  <tr>
                    {has("OFD_UPDATE") && (
                      <th className="px-4 py-3 text-left w-12">
                        <ThemeCheckbox
                          checked={
                            selectedOrders.length === orders.length &&
                            orders.length > 0
                          }
                          onChange={handleSelectAll}
                          isDark={isDark}
                        />
                      </th>
                    )}
                    {[
                      "ORDER ID",
                      "DATE",
                      "ORDER BY",
                      "NAME",
                      "PRODUCT",
                      "CATEGORY",
                      "REST AMT.",
                      "ORDER CAMPAIGN",
                      "STATUS",
                      "OFD BY",
                      "C.PARTNER",
                      "AWB No.",
                    ].map((col) => (
                      <th
                        key={col}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                    {(has("OFD_UPDATE") ||
                      isNdrAgent ||
                      isAdmin ||
                      isUserAdmin) && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">
                          CUSTOMER STATUS
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">
                          ACTION
                        </th>
                      </>
                    )}
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}
                >
                  {loading ? (
                    <tr>
                      <td colSpan={15} className="px-4 py-12 text-center">
                        <div className="flex items-center justify-center space-x-3">
                          <div
                            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                            style={{
                              borderColor: `${currentTheme.color} transparent transparent transparent`,
                            }}
                          />
                          <span className="text-gray-500">
                            Loading OFD orders...
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td
                        colSpan={15}
                        className="px-4 py-12 text-center text-gray-500"
                      >
                        No orders found in "{selectedStatusTab}" tab
                      </td>
                    </tr>
                  ) : (
                    orders.map((order, idx) => (
                      <tr
                        key={order.id}
                        className={`transition-colors ${
                          selectedOrders.includes(order.id)
                            ? isDark
                              ? "bg-teal-500/10"
                              : "bg-teal-50"
                            : isDark
                              ? idx % 2 === 0
                                ? "bg-[#111827]"
                                : "bg-white/[0.02]"
                              : idx % 2 === 0
                                ? "bg-white"
                                : "bg-gray-50/50"
                        } ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
                      >
                        {/* Checkbox */}
                        {has("OFD_UPDATE") && (
                          <td
                            className="px-4 py-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ThemeCheckbox
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => handleSelectOrder(order.id)}
                              isDark={isDark}
                            />
                          </td>
                        )}

                        {/* Order ID */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}
                          >
                            {order.orderId || order.id}
                          </span>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"} whitespace-nowrap`}
                          >
                            {order.orderDate || order.createdAt
                              ? formatDate(order.orderDate || order.createdAt)
                              : "-"}
                          </span>
                        </td>

                        {/* Order By */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span
                              className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}
                            >
                              {order.createdByName || "-"}
                            </span>
                            <span
                              className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              Emp: {order.createdById || "-"}
                            </span>
                          </div>
                        </td>

                        {/* Name + Phone */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span
                              className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}
                            >
                              {order.customerName || "-"}
                            </span>
                            <span
                              className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              {order.contactNumber || "-"}
                            </span>
                          </div>
                        </td>

                        {/* Product */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"} truncate block max-w-[150px]`}
                          >
                            {Array.isArray(order.orderItems) &&
                            order.orderItems.length > 0
                              ? order.orderItems
                                  .map((i) => i.productName)
                                  .join(", ")
                              : "-"}
                          </span>
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {order.category || "-"}
                          </span>
                        </td>

                        {/* Rest Amount */}
                        <td className="px-4 py-3">
                          <span
                            className="text-sm font-semibold"
                            style={{ color: currentTheme.color }}
                          >
                            ₹{" "}
                            {(
                              order.restAmount ||
                              order.grandTotal ||
                              0
                            ).toLocaleString()}
                          </span>
                        </td>

                        {/* Campaign */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {order.campaignName || "-"}
                          </span>
                        </td>

                        {/* Status (courier status) */}
                        <td className="px-4 py-3">
                          <span
                            className={`px-3 py-1 text-xs rounded-full font-medium ${isDark ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-blue-50 text-blue-600"}`}
                          >
                            {order.status === "OUT_FOR_DELIVERY"
                              ? "Out for delivery"
                              : order.status || "OFD"}
                          </span>
                        </td>

                        {/* OFD By */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {order.ofdAssignedToName || "-/-"}
                          </span>
                        </td>

                        {/* C.Partner */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {order.courierName || "-"}
                          </span>
                        </td>

                        {/* AWB */}
                        <td className="px-4 py-3">
                          <div
                            className={`flex items-center space-x-2 w-fit px-3 py-1.5 rounded-lg ${isDark ? "bg-white/5 border border-white/5" : "bg-gray-100"}`}
                          >
                            <span
                              className={`text-sm font-mono font-bold ${isDark ? "text-white" : "text-blue-600"}`}
                            >
                              {maskAWBNumber(order.awbNumber) || "-"}
                            </span>
                            {order.awbNumber && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(order.awbNumber);
                                }}
                                className={`p-1 rounded transition-colors ${isDark ? "hover:bg-white/10" : "hover:bg-white"}`}
                              >
                                <Copy className="w-3.5 h-3.5 text-gray-400" />
                              </button>
                            )}
                          </div>
                        </td>

                        {/* Customer Status Dropdown + Action */}
                        {(has("OFD_UPDATE") ||
                          isNdrAgent ||
                          isAdmin ||
                          isUserAdmin) && (
                          <>
                            <td
                              className="px-4 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* ✅ Show current customerStatus + allow change */}
                              <select
                                value={order.customerStatus || ""}
                                onChange={(e) => {
                                  if (e.target.value)
                                    handleCustomerStatusUpdate(
                                      order.id,
                                      e.target.value,
                                    );
                                }}
                                className={`text-sm px-3 py-1.5 rounded-lg border cursor-pointer focus:outline-none transition-all ${
                                  isDark
                                    ? "bg-[#1f2937] border-white/10 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                              >
                                <option value="">Select Status</option>
                                {CUSTOMER_STATUS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td
                              className="px-4 py-3 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => handleViewDetails(order)}
                                className="p-2 rounded-full transition-colors"
                                style={{
                                  backgroundColor: `${currentTheme.color}25`,
                                }}
                              >
                                <Eye
                                  className="w-4 h-4"
                                  style={{ color: currentTheme.color }}
                                />
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalItems={totalOrders}
                itemsPerPage={parseInt(orderLimit)}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  currentPageRef.current = page;
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  fetchOFDOrders({ overridePage: page });
                }}
              />
            </div>
          </div>

          {/* ── Assign Panel ── */}
          {(isAdmin || isUserAdmin) && has("OFD_UPDATE") && (
            <div
              className={`w-72 rounded-xl shadow-sm border h-fit sticky top-4 ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}
            >
              <div
                className={`p-4 border-b ${isDark ? "border-white/5" : "border-gray-200"}`}
              >
                <h3
                  className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}
                >
                  Assign to
                </h3>
                {selectedOrders.length > 0 && (
                  <p
                    className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {selectedOrders.length} order(s) selected
                  </p>
                )}
              </div>

              <div className="p-4 max-h-[500px] overflow-y-auto">
                <div className="space-y-2">
                  {employees.map((emp) => {
                    const empId = emp.id ?? emp.employeeId ?? emp.empId;
                    const selId =
                      selectedEmployee?.id ??
                      selectedEmployee?.employeeId ??
                      selectedEmployee?.empId;
                    const isSel = selId === empId;
                    return (
                      <button
                        key={empId}
                        onClick={() => setSelectedEmployee(isSel ? null : emp)}
                        className="w-full flex items-center space-x-3 p-3 rounded-lg transition-colors text-left border-2"
                        style={{
                          borderColor: isSel
                            ? currentTheme.color
                            : "transparent",
                          backgroundColor: isSel
                            ? `${currentTheme.color}15`
                            : "transparent",
                        }}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: isSel
                              ? currentTheme.color
                              : isDark
                                ? "rgba(255,255,255,0.1)"
                                : "#e5e7eb",
                          }}
                        >
                          {isSel ? (
                            <Check className="w-4 h-4 text-white" />
                          ) : (
                            <Plus
                              className={`w-4 h-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                            />
                          )}
                        </div>
                        <div>
                          <p
                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-800"}`}
                          >
                            {emp.name || emp.fullName || "-"}
                          </p>
                          <p
                            className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            Emp: {emp.employeeId || emp.id}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                  {employees.length === 0 && (
                    <p
                      className={`text-sm text-center py-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      No employees found
                    </p>
                  )}
                </div>
              </div>

              <div
                className={`p-4 border-t ${isDark ? "border-white/5" : "border-gray-200"}`}
              >
                <button
                  onClick={handleAssignOrders}
                  disabled={!selectedEmployee || selectedOrders.length === 0}
                  className={`w-full py-3 rounded-lg font-medium text-base transition-colors ${
                    selectedEmployee && selectedOrders.length > 0
                      ? "text-white hover:opacity-90"
                      : (isDark
                          ? "bg-white/5 text-gray-600"
                          : "bg-gray-200 text-gray-400") + " cursor-not-allowed"
                  }`}
                  style={
                    selectedEmployee && selectedOrders.length > 0
                      ? { backgroundColor: currentTheme.color }
                      : {}
                  }
                >
                  Assign{" "}
                  {selectedOrders.length > 0
                    ? `(${selectedOrders.length})`
                    : ""}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OFDOrders;
